import { ApiError, type CashoutRequest, type PortalMoney } from "@khazzin/api-client";
import { formatDate } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import { MoneyInput } from "@khazzin/ui/patterns/form";
import { EmptyState, KeyValueList } from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { type Column, DataTable } from "@khazzin/ui/patterns/table";
import { Button, ConfirmDialog, Dialog, Field } from "@khazzin/ui/primitives";
import type { Tone } from "@khazzin/ui/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { useState } from "react";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { useStoreScope } from "../../app/shell/scope";
import { financeKey } from "./FinancePage";

/**
 * Cashout requests in the Store Portal (EP10-S09, DA-10-09): Request cashout
 * from the balance card and the requests above the Cashouts tab, each with
 * Cancel while it is still `requested`.
 *
 * **Behind the MFA gate.** Without a second factor the button opens an "Enrol
 * MFA" prompt that sends the person through the provider's sign-in, where
 * the factor is enrolled, instead of the form; the API refuses the request
 * with `403 mfa_required` either way.
 */

const REQUEST_TONES: Record<CashoutRequest["status"], Tone> = {
	requested: "warning",
	approved: "info",
	paid: "success",
	rejected: "danger",
	cancelled: "neutral",
};

function requestsKey(storeId: string) {
	return [...financeKey(storeId), "cashout-requests"] as const;
}

/** The balance card's Request cashout: the form, or the Enrol MFA prompt. */
export function RequestCashoutButton({ available }: { available: PortalMoney }) {
	const { t } = useLocale();
	const { context } = useStoreScope();
	const [open, setOpen] = useState(false);
	const enrolled = context?.mfa?.enrolled === true;
	return (
		<>
			<Button variant="primary" className="min-h-12 md:min-h-8" onClick={() => setOpen(true)}>
				{t("portal.finance.cashout.request")}
			</Button>
			{open && enrolled && (
				<CashoutRequestDialog available={available} open onOpenChange={setOpen} />
			)}
			{open && !enrolled && <EnrolMfaDialog open onOpenChange={setOpen} />}
		</>
	);
}

function EnrolMfaDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const { t } = useLocale();
	const { signIn } = useAuth();
	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			title={t("portal.finance.cashout.mfa.title")}
			description={t("portal.finance.cashout.mfa.body")}
			size="sm"
			closeLabel={t("ui.actions.close")}
			footer={
				<>
					<Button variant="ghost" onClick={() => onOpenChange(false)}>
						{t("ui.actions.cancel")}
					</Button>
					<Button
						variant="primary"
						onClick={() => void signIn({ state: { returnTo: window.location.pathname } })}
					>
						{t("portal.finance.cashout.mfa.enrol")}
					</Button>
				</>
			}
		/>
	);
}

export function CashoutRequestDialog({
	available,
	open,
	onOpenChange,
}: {
	available: PortalMoney;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const { store } = useStoreScope();
	const storeId = store?.storeId ?? "";
	// One key per opening: a double-tapped Request replays the first.
	const [key] = useState(() => `cashout-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
	const [amount, setAmount] = useState<number | null>(available.amountMinor);
	const [failed, setFailed] = useState<{ message: string; field?: string } | null>(null);
	const requests = useQuery({
		queryKey: requestsKey(storeId),
		queryFn: ({ signal }) => api.listPortalCashoutRequests({ signal }),
		enabled: storeId !== "",
	});
	const destination = requests.data?.destination;
	const over = amount !== null && amount > available.amountMinor;
	const amountError =
		failed?.field === "amountMinor"
			? failed.message
			: over
				? t("portal.finance.cashout.overAvailable")
				: undefined;

	return (
		<ConfirmDialog
			open={open}
			onOpenChange={onOpenChange}
			title={t("portal.finance.cashout.title")}
			description={t("portal.finance.cashout.body")}
			confirmLabel={t("portal.finance.cashout.submit")}
			cancelLabel={t("ui.actions.cancel")}
			closeLabel={t("ui.actions.close")}
			confirmDisabled={amount === null || amount <= 0 || over}
			onConfirm={async () => {
				setFailed(null);
				try {
					await api.createPortalCashoutRequest(amount ?? 0, key);
					await queries.invalidateQueries({ queryKey: financeKey(storeId) });
				} catch (error) {
					setFailed(
						error instanceof ApiError
							? { message: error.message, ...(error.field ? { field: error.field } : {}) }
							: { message: t("portal.finance.cashout.failed") },
					);
					throw error;
				}
			}}
		>
			<KeyValueList
				rows={[
					{
						label: t("portal.finance.balance.available"),
						value: formatMoney(available, locale),
					},
					{
						label: t("portal.finance.cashout.destination"),
						value: destination?.label ?? t("portal.finance.cashout.noDestination"),
					},
				]}
			/>
			<Field
				label={t("portal.finance.cashout.amount")}
				required
				{...(amountError === undefined ? {} : { error: amountError })}
			>
				{(control) => (
					<MoneyInput
						{...control}
						currency={available.currency}
						locked
						min={1}
						value={amount}
						onChange={setAmount}
					/>
				)}
			</Field>
			{failed !== null && failed.field !== "amountMinor" && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{failed.message}
				</p>
			)}
		</ConfirmDialog>
	);
}

/** The store's requests, above the Cashouts tab; Cancel while `requested`. */
export function CashoutRequestsTable({
	storeId,
	timeZone,
	canWrite,
}: {
	storeId: string;
	timeZone: string;
	canWrite: boolean;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const [cancelling, setCancelling] = useState<CashoutRequest | null>(null);
	const [failed, setFailed] = useState<string | null>(null);
	const list = useQuery({
		queryKey: requestsKey(storeId),
		queryFn: ({ signal }) => api.listPortalCashoutRequests({ signal }),
	});
	const items = list.data?.items ?? [];
	const columns: Column<CashoutRequest>[] = [
		{
			id: "requested",
			headerKey: "portal.finance.cashout.requestedOn",
			card: "wide",
			cell: (r) => formatDate(r.requestedAt, { locale, timeZone }),
		},
		{
			id: "amount",
			headerKey: "portal.finance.cashout.amount",
			align: "end",
			cell: (r) => formatMoney({ amountMinor: r.amountMinor, currency: r.currency }, locale),
		},
		{
			id: "status",
			headerKey: "portal.finance.cashouts.status",
			cell: (r) => (
				<StatusChip tone={REQUEST_TONES[r.status]}>
					{t(`portal.finance.cashout.state.${r.status}`)}
				</StatusChip>
			),
		},
		{
			id: "cashout",
			headerKey: "portal.finance.payouts.cashout",
			mono: true,
			cell: (r) => r.cashoutNumber ?? "—",
		},
		...(canWrite
			? [
					{
						id: "actions",
						headerKey: "portal.finance.cashout.actions",
						cell: (r: CashoutRequest) =>
							r.status === "requested" ? (
								<Button size="sm" variant="secondary" onClick={() => setCancelling(r)}>
									{t("portal.finance.cashout.cancel")}
								</Button>
							) : null,
					} satisfies Column<CashoutRequest>,
				]
			: []),
	];
	return (
		<section aria-label={t("portal.finance.cashout.requests")} className="flex flex-col gap-2">
			<h2 className="text-[14px] font-bold text-fg">{t("portal.finance.cashout.requests")}</h2>
			{failed !== null && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{failed}
				</p>
			)}
			<DataTable<CashoutRequest>
				screenKey="portal.finance.cashoutRequests"
				caption={t("portal.finance.cashout.requests")}
				t={t}
				columns={columns}
				rowKey={(r) => r.requestId}
				page={{ items, nextCursor: null }}
				status={list.isLoading ? "loading" : list.isError ? "error" : "success"}
				error={list.error}
				onRetry={() => void list.refetch()}
				pagination={{
					showingFrom: items.length === 0 ? 0 : 1,
					showingTo: items.length,
					hasNext: false,
					hasPrevious: false,
					onNext: () => {},
					onPrevious: () => {},
				}}
				empty={<EmptyState title={t("portal.finance.cashout.empty")} illustration="none" />}
			/>
			{cancelling !== null && (
				<ConfirmDialog
					open
					onOpenChange={(open) => !open && setCancelling(null)}
					title={t("portal.finance.cashout.cancelTitle")}
					description={t("portal.finance.cashout.cancelBody", {
						amount: formatMoney(
							{ amountMinor: cancelling.amountMinor, currency: cancelling.currency },
							locale,
						),
					})}
					confirmLabel={t("portal.finance.cashout.cancel")}
					cancelLabel={t("portal.finance.cashout.keep")}
					closeLabel={t("ui.actions.close")}
					onConfirm={async () => {
						setFailed(null);
						try {
							await api.cancelPortalCashoutRequest(cancelling.requestId);
							await queries.invalidateQueries({ queryKey: financeKey(storeId) });
						} catch (error) {
							setFailed(
								error instanceof ApiError
									? error.message
									: t("portal.finance.cashout.cancelFailed"),
							);
							throw error;
						}
					}}
				/>
			)}
		</section>
	);
}
